import { useId, useState, type ReactNode } from 'react';

import { Badge } from '../Badge';
import { Icon } from '../Icon';
import { aiSourceLabel, roleLabel } from '../vocabulary';
import { DraftBanner } from './DraftBanner';
import type { AiStructuredResponseData } from './types';

/** The seven sections, in the order §2.3 fixes them. */
const SECTION_TITLES = [
  'Situation',
  'Known facts',
  'Unknown or missing',
  'Recommended next step',
  'Responsible human',
  'Draft',
  'Risks and escalations',
] as const;

/** Exported so tests and later stages can assert the format has not drifted. */
export const AI_RESPONSE_SECTIONS: readonly string[] = SECTION_TITLES;

const DATE_LABEL = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

function formatDate(iso: string): string {
  const parsed = new Date(`${iso}T00:00:00.000Z`);
  return Number.isNaN(parsed.getTime()) ? iso : DATE_LABEL.format(parsed);
}

function Section({
  title,
  children,
  empty,
}: {
  title: string;
  children: ReactNode;
  empty?: boolean;
}): React.JSX.Element {
  return (
    <section className="fo-ai__section" data-empty={empty === true ? '' : undefined}>
      {/* h3: the answer sits inside a card or a drawer panel, both of which are
          labelled by an h2, so its sections are one level below that. */}
      <h3 className="fo-ai__section-title micro-label">{title}</h3>
      {children}
    </section>
  );
}

export interface AiStructuredResponseProps {
  /** The answer to render. */
  readonly response: AiStructuredResponseData;
  /** Accessible name for the whole block. */
  readonly label?: string;
}

/**
 * Renders an assistant answer as seven labelled sections.
 *
 * Never a text blob, and the structure is not cosmetic. Each section answers a
 * question a reviewer would otherwise have to ask: what do you think is going
 * on, what did you actually read, what do you *not* know, what would you do,
 * who decides, what did you write, and what could go wrong. A paragraph can
 * skip any of those silently. A set of sections that always renders — empty
 * ones included, marked as empty — cannot.
 *
 * Every known fact carries a link to the record it came from and the date that
 * record bears. Those two chips are what let a manager check the assistant in
 * seconds instead of trusting it, and they are the reason "known facts" is a
 * list of citations rather than a paragraph of prose.
 *
 * The draft is collapsed and separately labelled, so text the assistant wrote
 * can never be read as text the business has sent.
 *
 * @param props see {@link AiStructuredResponseProps}
 * @returns the structured answer
 */
export function AiStructuredResponse({
  response,
  label = 'Assistant answer',
}: AiStructuredResponseProps): React.JSX.Element {
  const [draftOpen, setDraftOpen] = useState(false);
  const draftId = `${useId()}-draft`;

  return (
    <article className="fo-ai" aria-label={label}>
      <header className="fo-ai__header">
        <Icon name="assistant" className="fo-ai__glyph" />
        <p className="fo-ai__provenance">
          Generated by the assistant ·{' '}
          <time dateTime={response.generatedAt}>{response.generatedAt}</time>
        </p>
      </header>

      <Section title={SECTION_TITLES[0]}>
        <p className="fo-ai__prose">{response.situation}</p>
      </Section>

      <Section title={SECTION_TITLES[1]} empty={response.knownFacts.length === 0}>
        {response.knownFacts.length === 0 ? (
          <p className="fo-ai__none">No facts could be read from the records.</p>
        ) : (
          <ul className="fo-ai__facts">
            {response.knownFacts.map((fact) => (
              <li key={`${fact.source.href}-${fact.statement}`} className="fo-ai__fact">
                <p className="fo-ai__prose">{fact.statement}</p>
                <p className="fo-ai__citation">
                  <a className="fo-ai__source" href={fact.source.href} title={fact.source.recordId}>
                    <Icon name="link" size="0.9em" />
                    {fact.source.label}
                  </a>
                  <Badge tone="neutral" subtle>
                    {aiSourceLabel(fact.source.recordType)}
                  </Badge>
                  <Badge tone="neutral" subtle>
                    <time dateTime={fact.source.observedOn}>
                      {formatDate(fact.source.observedOn)}
                    </time>
                  </Badge>
                </p>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title={SECTION_TITLES[2]} empty={response.unknowns.length === 0}>
        {response.unknowns.length === 0 ? (
          <p className="fo-ai__none">Nothing was flagged as missing.</p>
        ) : (
          <ul className="fo-ai__list">
            {response.unknowns.map((unknown) => (
              <li key={unknown}>{unknown}</li>
            ))}
          </ul>
        )}
      </Section>

      <Section title={SECTION_TITLES[3]}>
        <p className="fo-ai__suggestion">
          <span className="fo-ai__suggestion-tag">Suggestion</span>
          {response.recommendedNextStep}
        </p>
      </Section>

      <Section title={SECTION_TITLES[4]}>
        <p className="fo-ai__responsible">
          <Badge tone="accent">{roleLabel(response.responsibleHuman.role)}</Badge>
          {response.responsibleHuman.name === undefined ? null : (
            <span className="fo-ai__responsible-name">{response.responsibleHuman.name}</span>
          )}
          <span className="fo-ai__responsible-because">{response.responsibleHuman.because}</span>
        </p>
      </Section>

      <Section title={SECTION_TITLES[5]} empty={response.draft === undefined}>
        {response.draft === undefined ? (
          <p className="fo-ai__none">No draft was produced.</p>
        ) : (
          <div className="fo-ai__draft">
            <DraftBanner subject="Assistant draft" />
            <button
              type="button"
              className="fo-ai__disclosure"
              aria-expanded={draftOpen}
              aria-controls={draftId}
              onClick={() => {
                setDraftOpen((open) => !open);
              }}
            >
              <Icon name={draftOpen ? 'chevron-down' : 'chevron-right'} size="1em" />
              {response.draft.title}
            </button>
            <div className="fo-ai__draft-body" id={draftId} hidden={!draftOpen}>
              <p className="fo-ai__prose">{response.draft.body}</p>
            </div>
          </div>
        )}
      </Section>

      <Section title={SECTION_TITLES[6]} empty={response.risks.length === 0}>
        {response.risks.length === 0 ? (
          <p className="fo-ai__none">No risks or escalations were identified.</p>
        ) : (
          <ul className="fo-ai__list">
            {response.risks.map((risk) => (
              <li key={risk.statement} className="fo-ai__risk">
                <Badge tone={risk.kind === 'escalation' ? 'danger' : 'warning'} subtle>
                  {risk.kind === 'escalation' ? 'Escalation' : 'Risk'}
                </Badge>
                {risk.statement}
              </li>
            ))}
          </ul>
        )}
      </Section>
    </article>
  );
}
